import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, DonkeyMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Pencil, ArrowRight, Loader2 } from "lucide-react";
import EvidencePanel from "@/components/EvidencePanel";

const SECTIONS = [
  ["experience", "Experience"],
  ["skills", "Skills"],
  ["credibility", "Credibility"],
  ["resources", "Resources"],
  ["objectives", "Objectives"],
  ["constraints", "Constraints"],
  ["customer_access", "Customer access"],
  ["preferences", "Preferences"],
];

export default function Review() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/interviews/${id}`);
        if (!data.profile_summary) {
          const res = await api.post(`/interviews/${id}/summary`);
          setSummary(res.data.summary);
        } else {
          setSummary(data.profile_summary);
        }
      } catch (e) {
        toast.error("Could not load your summary.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const updateItem = (key, idx, val) =>
    setSummary((s) => ({ ...s, [key]: s[key].map((x, i) => (i === idx ? val : x)) }));
  const removeItem = (key, idx) =>
    setSummary((s) => ({ ...s, [key]: s[key].filter((_, i) => i !== idx) }));
  const addItem = (key) => {
    const val = (adding[key] || "").trim();
    if (!val) return;
    setSummary((s) => ({ ...s, [key]: [...(s[key] || []), val] }));
    setAdding((a) => ({ ...a, [key]: "" }));
  };

  const saveSummary = async () => {
    await api.put(`/interviews/${id}/summary`, { summary });
  };

  const judge = async () => {
    setBusy(true);
    try {
      await saveSummary();
      if (!user) {
        localStorage.setItem("guest_interview_id", id);
        navigate(`/register?interview=${id}&next=/review/${id}`);
        return;
      }
      const { data } = await api.post(`/interviews/${id}/analyse`);
      navigate(`/analysis/${data.report_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start analysis.");
      setBusy(false);
    }
  };

  if (loading || !summary) {
    return (
      <div className="min-h-screen bg-donkey-bg grain flex flex-col items-center justify-center gap-5">
        <DonkeyMark size={56} className="animate-pulse-soft" />
        <p className="text-donkey-grey">Gathering what you told me…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-donkey-bg grain">
      <div className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 h-16 flex items-center"><Logo /></div>
      </div>
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-donkey-warm">
          What the Donkey heard
        </h1>
        <p className="mt-4 text-donkey-grey border-l-2 border-donkey-orange/50 pl-4 leading-relaxed">
          This is what I think you told me. Correct anything I have misunderstood before I judge your options.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {SECTIONS.map(([key, label]) => (
            <div key={key} className="card-donkey rounded-2xl p-6" data-testid={`summary-${key}`}>
              <h3 className="font-heading font-semibold text-donkey-warm mb-4 flex items-center gap-2">
                <Pencil className="w-4 h-4 text-donkey-orange" /> {label}
              </h3>
              <ul className="space-y-2.5">
                {(summary[key] || []).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 group">
                    <Input value={item} onChange={(e) => updateItem(key, idx, e.target.value)}
                      className="bg-donkey-bg border-white/10 text-sm text-donkey-warm/90 h-9" />
                    <button onClick={() => removeItem(key, idx)}
                      className="text-donkey-grey hover:text-donkey-red mt-2 shrink-0" aria-label="Remove">
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
                {(!summary[key] || summary[key].length === 0) && (
                  <li className="text-xs text-donkey-grey italic">Nothing here yet.</li>
                )}
              </ul>
              <div className="mt-3 flex items-center gap-2">
                <Input value={adding[key] || ""} onChange={(e) => setAdding((a) => ({ ...a, [key]: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && addItem(key)}
                  placeholder="Add a point…" className="bg-donkey-bg border-white/10 text-sm h-9" />
                <button onClick={() => addItem(key)} className="text-donkey-orange hover:text-donkey-orange/80 shrink-0" aria-label="Add">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {user && <EvidencePanel interviewId={id} />}

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 card-donkey rounded-2xl p-6">
          <p className="text-sm text-donkey-grey">
            {user ? "Ready when you are. This is where the honest bit begins." : "You'll create an account next to save this and see your verdict."}
          </p>
          <Button onClick={judge} disabled={busy}
            className="bg-donkey-orange text-donkey-black hover:bg-donkey-orange/90 font-semibold px-7 h-12 shrink-0"
            data-testid="judge-options-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working…</>
              : <>Judge my options <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
